import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useToasts } from "react-toast-notifications";
import Swal from "sweetalert2";
import {
  MDBBadge,
  MDBBtn,
  MDBCard,
  MDBCardBody,
  MDBIcon,
  MDBTable,
} from "mdbreact";
import { GenericProducts } from "../../../../constants";
import { axioKit, fullName } from "../../../../services/utilities";
import RequestModal from "./requestModal";
import ActionModal from "./actionModal";
import ReceiveTable from "./receiveTable";
import PricesModal from "./pricesModal";
import {
  APPROVERS,
  PURCHASE_URL,
  STATUS_COLOR,
  TABS,
  formatDate,
  lineName,
  lineQty,
  peso,
  poNumber,
  qtyLabel,
} from "./common";

const PRODUCTS_URL = "commerce/merchandise/products";

/**
 * Purchase Orders of the branch:
 * request (staff) -> approve/deny (Sr. MLS / Chief MLS) -> receive (stock).
 * Received items are the stock that can be dispensed.
 */
// approver: approves whatever the role (e.g. the branch manager)
// initialTab: the status tab opened first, e.g. "approved" to receive deliveries
// mine: only the requests of the user (Supply Hub > My Requests)
export default function PurchaseOrders({
  approver = false,
  initialTab = "pending",
  mine = false,
  title = "Purchase Orders",
}) {
  const { token, activePlatform, auth } = useSelector(({ auth }) => auth),
    [orders, setOrders] = useState([]),
    [products, setProducts] = useState([]),
    [stock, setStock] = useState({}),
    [tab, setTab] = useState(initialTab),
    [year, setYear] = useState(String(new Date().getFullYear())),
    [expanded, setExpanded] = useState({}),
    [decisions, setDecisions] = useState({}), // { orderId: { itemId: "approve" | "deny" } }
    [quantities, setQuantities] = useState({}), // { itemId: qty to approve }
    [receiveQuantities, setReceiveQuantities] = useState({}), // { itemId: qty received }
    [freeQuantities, setFreeQuantities] = useState({}), // { itemId: free qty, e.g. 6+1 }
    [showRequest, setShowRequest] = useState(false),
    [action, setAction] = useState(null), // { mode, order }
    [pricing, setPricing] = useState(null), // order whose prices are edited
    [adding, setAdding] = useState(null), // order items are added to
    [isLoading, setIsLoading] = useState(false),
    { addToast } = useToasts();

  const branch = activePlatform?.branchId;
  const canApprove = approver || APPROVERS.includes(activePlatform?.role);

  const notify = useCallback(
    (message, appearance = "error") =>
      addToast(message, { appearance, autoDismiss: true }),
    [addToast],
  );

  const load = useCallback(() => {
    if (!branch || !token) return Promise.resolve();
    setIsLoading(true);
    return Promise.all([
      axioKit.universal(`${PURCHASE_URL}/browse`, token, { branch }),
      axioKit.universal(`${PRODUCTS_URL}/browse`, token, { branch }),
      axioKit.universal(`${PURCHASE_URL}/stock`, token, { branch }),
    ])
      .then(([_orders, _products, _stock]) => {
        setOrders(
          (_orders?.payload || []).filter(
            ({ userId }) =>
              !mine || String(userId?._id || userId) === String(auth?._id),
          ),
        );
        setProducts(_products?.payload || []);
        setStock(
          (_stock?.payload || []).reduce((result, entry) => {
            result[`${entry.product}:${entry.variant || ""}`] = entry;
            return result;
          }, {}),
        );
      })
      .catch((error) => notify(error.message))
      .finally(() => setIsLoading(false));
  }, [branch, token, notify, mine, auth]);

  useEffect(() => {
    load();
  }, [load]);

  // each Supply Hub page opens its own tab
  useEffect(() => setTab(initialTab), [initialTab]);

  const yearOf = ({ createdAt }) => String(new Date(createdAt).getFullYear());

  // years with orders, plus the current year, newest first
  const years = useMemo(
    () =>
      [
        ...new Set([String(new Date().getFullYear()), ...orders.map(yearOf)]),
      ].sort((a, b) => b - a),
    [orders],
  );

  // orders requested in the chosen year ("" = all years)
  const ofYear = useMemo(
    () => orders.filter((order) => !year || yearOf(order) === year),
    [orders, year],
  );

  const counts = useMemo(
    () =>
      TABS.reduce((result, { key, statuses }) => {
        result[key] = ofYear.filter(({ status }) =>
          statuses.includes(status),
        ).length;
        return result;
      }, {}),
    [ofYear],
  );

  const visible = useMemo(
    () =>
      ofYear.filter(({ status }) =>
        TABS.find(({ key }) => key === tab).statuses.includes(status),
      ),
    [ofYear, tab],
  );

  const toggle = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  // per item decision of a pending order, every item is approved until denied
  const decisionOf = (orderId, itemId) =>
    decisions[orderId]?.[itemId] || "approve";
  // qty to approve, the requested qty until changed (less or more)
  const approveQtyOf = ({ _id, qnty }) => quantities[_id] ?? qnty.request;
  // qty received, the approved qty until changed (short or more)
  const receiveQtyOf = ({ _id, qnty }) =>
    receiveQuantities[_id] ?? qnty.approved ?? qnty.request;
  // free items given with the delivery, same packaging unit
  const freeQtyOf = ({ _id }) => freeQuantities[_id] ?? 0;
  // denied, or approved with 0
  const isItemDenied = (order, item) =>
    decisionOf(order._id, item._id) === "deny" ||
    !(Number(approveQtyOf(item)) > 0);

  const decide = (orderId, itemId, value) =>
    setDecisions((prev) => ({
      ...prev,
      [orderId]: { ...prev[orderId], [itemId]: value },
    }));

  const run = async (request, success) => {
    setIsLoading(true);
    try {
      const result = await request();
      notify(result?.success || success, "success");
      await load();
      return true;
    } catch (error) {
      notify(error.message);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequest = async (data) => {
    const ok = await run(() =>
      axioKit.save(PURCHASE_URL, { ...data, branchId: branch }, token),
    );
    if (ok) {
      setShowRequest(false);
      setTab("pending");
    }
  };

  const handleAction = async (endpoint, data) => {
    const ok = await run(() =>
      axioKit.update(PURCHASE_URL, data, token, endpoint),
    );
    if (ok) {
      setAction(null);
      if (endpoint === "approve") setTab("approved");
      if (endpoint === "receive") setTab("received");
      if (endpoint === "deny") setTab("closed");
    }
  };

  // prices of the items, also after the P.O. is received
  const handlePrices = async (data) => {
    const ok = await run(() =>
      axioKit.update(PURCHASE_URL, data, token, "prices"),
    );
    if (ok) setPricing(null);
  };

  // more items on an approved or received P.O.
  const handleAddItems = async (data) => {
    const ok = await run(() =>
      axioKit.update(PURCHASE_URL, data, token, "items"),
    );
    if (ok) setAdding(null);
  };

  // the requester sees the reason, so it is required
  const handleDeny = (order) =>
    Swal.fire({
      title: `Deny ${poNumber(order)}?`,
      input: "text",
      inputPlaceholder: "Reason",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Deny P.O.",
      confirmButtonColor: "#d33",
      cancelButtonText: "Back",
      inputValidator: (value) =>
        !value?.trim() ? "Write the reason for denying." : undefined,
    }).then(async ({ isConfirmed, value }) => {
      if (!isConfirmed) return;
      const ok = await run(() =>
        axioKit.update(
          PURCHASE_URL,
          { _id: order._id, response: value.trim() },
          token,
          "deny",
        ),
      );
      if (ok) setTab("closed");
    });

  // approves the items at the qty set (requested, less or more); denied items get 0.
  // Every item denied = the whole request is denied (with a reason).
  const handleReview = (order) => {
    const denied = order.items.filter((item) => isItemDenied(order, item));
    if (denied.length === order.items.length) return handleDeny(order);

    const changed = order.items.filter(
      (item) =>
        !isItemDenied(order, item) &&
        Number(approveQtyOf(item)) !== item.qnty.request,
    );

    return Swal.fire({
      title: `Approve ${poNumber(order)}?`,
      html: `${order.items.length - denied.length} item(s) approved${
        changed.length ? ` (${changed.length} with a changed qty)` : ""
      }${
        denied.length
          ? `, <b style="color:#d33">${denied.length} denied</b>`
          : ""
      }.`,
      input: denied.length ? "text" : undefined,
      inputPlaceholder: "Reason for the denied item(s) (optional)",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Submit",
      cancelButtonText: "Back",
    }).then(async ({ isConfirmed, value }) => {
      if (!isConfirmed) return;
      const ok = await run(() =>
        axioKit.update(
          PURCHASE_URL,
          {
            _id: order._id,
            items: order.items.map((item) => {
              const { _id } = item;
              const isDenied = isItemDenied(order, item);
              return {
                _id,
                approved: isDenied ? 0 : Number(approveQtyOf(item)),
                ...(isDenied && value?.trim() && { remarks: value.trim() }),
              };
            }),
          },
          token,
          "approve",
        ),
      );
      if (ok) setTab("approved");
    });
  };

  // receives the qty entered on the list; brands with lot/expiry open the
  // receive form (prefilled) since their lots are required
  const handleReceive = (order) => {
    const items = order.items.filter(({ qnty }) => qnty.approved !== 0);
    const totalOf = (item) =>
      (Number(receiveQtyOf(item)) || 0) + (Number(freeQtyOf(item)) || 0);
    const withLots = items.some(
      (item) => item.productId?.tracksLot && totalOf(item) > 0,
    );
    if (withLots) return setAction({ mode: "receive", order });

    const total = items.reduce((sum, item) => sum + totalOf(item), 0);
    return Swal.fire({
      title: `Receive ${poNumber(order)}?`,
      text: total
        ? "The received quantities will be added to the stock."
        : "Nothing was received; the P.O. will be closed as received with 0.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Receive",
      cancelButtonText: "Back",
    }).then(({ isConfirmed }) => {
      if (!isConfirmed) return;
      handleAction("receive", {
        _id: order._id,
        received: new Date().toISOString().slice(0, 10),
        items: items.map((item) => ({
          _id: item._id,
          received: Math.max(Number(receiveQtyOf(item)) || 0, 0),
          freeItem: Math.max(Number(freeQtyOf(item)) || 0, 0),
          lots: [],
        })),
      });
    });
  };

  const handleCancel = (order) =>
    Swal.fire({
      title: `Cancel ${poNumber(order)}?`,
      input: "text",
      inputPlaceholder: "Reason (optional)",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Cancel P.O.",
      cancelButtonText: "Back",
    }).then(({ isConfirmed, value }) => {
      if (isConfirmed)
        handleAction("cancel", {
          _id: order._id,
          response: value || undefined,
        });
    });

  return (
    <MDBCard narrow className="pb-3">
      <MDBCardBody>
        {/* header */}
        <div
          className="d-flex flex-wrap justify-content-between align-items-center mb-3"
          style={{ gap: 8 }}
        >
          <div>
            <h5 className="mb-0">
              <MDBIcon icon="file-invoice" className="mr-2 text-primary" />
              {title}
            </h5>
            <small className="text-muted">
              Request →{" "}
              {canApprove ? "approve" : "approval by Sr. MLS / Chief MLS"} →
              receive. Received items become dispensable stock.
            </small>
          </div>
          <MDBBtn
            size="sm"
            color="primary"
            className="m-0"
            onClick={() => setShowRequest(true)}
            disabled={!branch}
          >
            <MDBIcon icon="cart-plus" className="mr-1" /> New request
          </MDBBtn>
        </div>

        {/* tabs + year */}
        <div
          className="d-flex flex-wrap justify-content-between align-items-center mb-3"
          style={{ gap: 8 }}
        >
          <div className="btn-group flex-wrap" role="group" aria-label="Status">
            {TABS.map(({ key, label, icon }) => (
              <button
                key={key}
                type="button"
                className={`btn btn-sm m-0 z-depth-0 ${
                  tab === key ? "btn-primary" : "btn-outline-primary"
                }`}
                style={{ textTransform: "none" }}
                onClick={() => setTab(key)}
              >
                <MDBIcon icon={icon} className="mr-1" />
                {label}
                {counts[key] > 0 && (
                  <MDBBadge
                    color={tab === key ? "light" : "primary"}
                    className="ml-2"
                  >
                    {counts[key]}
                  </MDBBadge>
                )}
              </button>
            ))}
          </div>
          <div className="d-flex align-items-center">
            <MDBIcon icon="calendar-alt" className="text-muted mr-2" />
            <select
              className="browser-default custom-select custom-select-sm"
              style={{ width: 130 }}
              value={year}
              onChange={({ target }) => setYear(target.value)}
              aria-label="Year"
            >
              {years.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
              <option value="">All years</option>
            </select>
          </div>
        </div>

        {!visible.length && (
          <div className="border rounded text-center text-muted py-5">
            {isLoading
              ? "Loading..."
              : `No purchase orders here${year ? ` for ${year}` : ""}.`}
          </div>
        )}

        {visible.map((order, index) => {
          const isOpen = !!expanded[order._id];
          // approvers decide per item right on the list
          const isReviewing = order.status === "pending" && canApprove;
          // approved: received qty is entered right on the list
          const isReceiving = order.status === "approved";
          return (
            <div key={order._id} className="border rounded mb-2">
              <div
                role="button"
                tabIndex={0}
                className={`px-3 py-2 d-flex flex-wrap justify-content-between align-items-center ${
                  isOpen ? "blue lighten-5" : "grey lighten-5"
                }`}
                style={{ gap: 8, cursor: "pointer" }}
                onClick={() => toggle(order._id)}
                onKeyDown={(e) => e.key === "Enter" && toggle(order._id)}
              >
                <div className="d-flex align-items-center">
                  <MDBIcon
                    icon={isOpen ? "chevron-down" : "chevron-right"}
                    className="text-muted mr-3"
                    style={{ width: 12 }}
                  />
                  <div>
                    <b className="mr-2">
                      <span className="text-muted mr-1">{index + 1}.</span>
                      {poNumber(order)}
                    </b>
                    <MDBBadge color={STATUS_COLOR[order.status]}>
                      {order.status === "cancel" ? "cancelled" : order.status}
                    </MDBBadge>
                    <small className="d-block text-muted">
                      Requested {formatDate(order.createdAt)}
                      {order.userId?.fullName
                        ? ` by ${fullName(order.userId.fullName)}`
                        : ""}
                      {order.expected
                        ? ` · expected ${formatDate(order.expected)}`
                        : ""}
                      {order.approvedBy?.fullName &&
                        ` · ${order.status === "denied" ? "denied" : "approved"} by ${fullName(
                          order.approvedBy.fullName,
                        )}`}
                      {order.receivedBy?.fullName &&
                        ` · received ${formatDate(order.received)} by ${fullName(
                          order.receivedBy.fullName,
                        )}`}
                    </small>
                  </div>
                </div>
                {/* the buttons do not open/close the order */}
                <div
                  className="d-flex align-items-center"
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  <MDBBadge color="light" className="mr-2">
                    {order.items.length} item
                    {order.items.length === 1 ? "" : "s"}
                  </MDBBadge>
                  {order.status === "pending" && canApprove && (
                    <>
                      <MDBBtn
                        size="sm"
                        color="success"
                        className="my-0"
                        title="Review the quantities and approve"
                        onClick={() => setAction({ mode: "approve", order })}
                      >
                        <MDBIcon icon="check" className="mr-1" /> Approve
                      </MDBBtn>
                      <MDBBtn
                        size="sm"
                        color="danger"
                        className="my-0"
                        onClick={() => handleDeny(order)}
                      >
                        <MDBIcon icon="ban" className="mr-1" /> Deny
                      </MDBBtn>
                    </>
                  )}
                  {order.status === "approved" && (
                    <MDBBtn
                      size="sm"
                      color="primary"
                      className="my-0"
                      onClick={() => setAction({ mode: "receive", order })}
                    >
                      <MDBIcon icon="truck" className="mr-1" /> Receive
                    </MDBBtn>
                  )}
                  {["approved", "received"].includes(order.status) && (
                    <>
                      <MDBBtn
                        size="sm"
                        color="light"
                        className="my-0"
                        title="Set or correct the price of the items"
                        onClick={() => setPricing(order)}
                      >
                        <MDBIcon icon="tags" className="mr-1" /> Prices
                      </MDBBtn>
                      <MDBBtn
                        size="sm"
                        color="light"
                        className="my-0"
                        title={
                          order.status === "received"
                            ? "Add items delivered with this P.O.; they go to the stock"
                            : "Add items to this P.O."
                        }
                        onClick={() => setAdding(order)}
                      >
                        <MDBIcon icon="plus" className="mr-1" /> Add item
                      </MDBBtn>
                    </>
                  )}
                  {/* received before the inventory records existed */}
                  {order.status === "received" && (
                    <MDBBtn
                      size="sm"
                      color="light"
                      className="my-0 mr-0"
                      title="Make the inventory records (packs) of this P.O. if missing"
                      disabled={isLoading}
                      onClick={() =>
                        run(() =>
                          axioKit.update(
                            PURCHASE_URL,
                            { _id: order._id },
                            token,
                            "inventory",
                          ),
                        )
                      }
                    >
                      <MDBIcon icon="sync" className="mr-1" /> Sync inventory
                    </MDBBtn>
                  )}
                  {["pending", "approved"].includes(order.status) && (
                    <MDBBtn
                      size="sm"
                      color="light"
                      className="my-0 mr-0"
                      onClick={() => handleCancel(order)}
                    >
                      Cancel
                    </MDBBtn>
                  )}
                </div>
              </div>

              {isOpen && (
                <>
                  {isReceiving ? (
                    <ReceiveTable
                      order={order}
                      stock={stock}
                      receiveQtyOf={receiveQtyOf}
                      setReceiveQty={(itemId, qty) =>
                        setReceiveQuantities((prev) => ({
                          ...prev,
                          [itemId]: qty,
                        }))
                      }
                      freeQtyOf={freeQtyOf}
                      setFreeQty={(itemId, qty) =>
                        setFreeQuantities((prev) => ({
                          ...prev,
                          [itemId]: qty,
                        }))
                      }
                      onReceive={() => handleReceive(order)}
                      isLoading={isLoading}
                    />
                  ) : (
                    <MDBTable small className="mb-0">
                      <thead>
                        <tr className="small text-muted">
                          <th>Item</th>
                          <th>Requested</th>
                          <th>Approved</th>
                          <th>Received</th>
                          <th>Price</th>
                          <th>On hand</th>
                          {isReviewing && (
                            <th className="text-center" style={{ width: 170 }}>
                              Decision
                            </th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {order.items.map((item) => {
                          const generic = GenericProducts.find(item.pgid);
                          const onHand =
                            stock[
                              `${item.productId?._id}:${item.variant || ""}`
                            ]?.onHand || 0;
                          const isDenied =
                            isReviewing &&
                            decisionOf(order._id, item._id) === "deny";
                          return (
                            <tr
                              key={item._id}
                              className={isDenied ? "red lighten-5" : ""}
                              style={{
                                textDecoration: isDenied
                                  ? "line-through"
                                  : "none",
                              }}
                            >
                              <td>
                                {generic && (
                                  <code className="mr-1">
                                    {generic.abbreviation}
                                  </code>
                                )}
                                {lineName(item)}
                                {item.lots?.length > 0 && (
                                  <small className="d-block text-muted">
                                    {item.lots
                                      .map(
                                        ({ lot, expiry, qty }) =>
                                          `${lot || "no lot"}${
                                            expiry
                                              ? ` exp ${formatDate(expiry)}`
                                              : ""
                                          } ×${qty}`,
                                      )
                                      .join(" · ")}
                                  </small>
                                )}
                              </td>
                              <td className="small">
                                {qtyLabel(item.qnty.request, item)}
                              </td>
                              <td className="small">
                                {/* approver: qty to approve, less or more than requested */}
                                {isReviewing && !isDenied ? (
                                  <div
                                    className="input-group input-group-sm flex-nowrap"
                                    style={{ width: 150 }}
                                  >
                                    <div className="input-group-prepend">
                                      <button
                                        type="button"
                                        className="btn btn-outline-secondary m-0 px-2 py-0 z-depth-0"
                                        title="Less"
                                        onClick={() =>
                                          setQuantities((prev) => ({
                                            ...prev,
                                            [item._id]: Math.max(
                                              Number(approveQtyOf(item)) - 1,
                                              0,
                                            ),
                                          }))
                                        }
                                      >
                                        <MDBIcon icon="minus" />
                                      </button>
                                    </div>
                                    <input
                                      type="number"
                                      min={0}
                                      className="form-control text-center px-1"
                                      value={approveQtyOf(item)}
                                      onChange={({ target }) =>
                                        setQuantities((prev) => ({
                                          ...prev,
                                          [item._id]: target.value,
                                        }))
                                      }
                                    />
                                    <div className="input-group-append">
                                      <button
                                        type="button"
                                        className="btn btn-outline-secondary m-0 px-2 py-0 z-depth-0"
                                        title="More"
                                        onClick={() =>
                                          setQuantities((prev) => ({
                                            ...prev,
                                            [item._id]:
                                              Number(approveQtyOf(item)) + 1,
                                          }))
                                        }
                                      >
                                        <MDBIcon icon="plus" />
                                      </button>
                                    </div>
                                  </div>
                                ) : /* approved 0 on a reviewed order = item denied */
                                item.qnty.approved === 0 &&
                                  order.status !== "pending" ? (
                                  <>
                                    <MDBBadge color="danger">denied</MDBBadge>
                                    {item.remarks?.approved && (
                                      <span className="d-block text-muted">
                                        {item.remarks.approved}
                                      </span>
                                    )}
                                  </>
                                ) : (
                                  qtyLabel(item.qnty.approved, item)
                                )}
                              </td>
                              <td className="small">
                                {qtyLabel(item.qnty.received, item)}
                                {item.freeItem > 0 && (
                                  <span className="d-block text-success">
                                    + {item.freeItem} free
                                  </span>
                                )}
                              </td>
                              <td className="small">
                                {item.price > 0 ? (
                                  <>
                                    {peso(item.price)}
                                    <span className="d-block text-muted">
                                      {peso(item.price * lineQty(item))}
                                    </span>
                                  </>
                                ) : (
                                  "—"
                                )}
                              </td>
                              <td className="small">
                                <MDBBadge
                                  color={onHand > 0 ? "success" : "light"}
                                >
                                  {onHand}
                                </MDBBadge>
                              </td>
                              {isReviewing && (
                                <td
                                  className="text-center py-1"
                                  style={{ textDecoration: "none" }}
                                >
                                  <div className="btn-group" role="group">
                                    {[
                                      ["approve", "check", "success"],
                                      ["deny", "ban", "danger"],
                                    ].map(([key, icon, color]) => {
                                      const active =
                                        decisionOf(order._id, item._id) === key;
                                      return (
                                        <button
                                          key={key}
                                          type="button"
                                          className={`btn btn-sm m-0 px-2 py-1 z-depth-0 ${
                                            active
                                              ? `btn-${color}`
                                              : `btn-outline-${color}`
                                          }`}
                                          style={{ textTransform: "none" }}
                                          onClick={() =>
                                            decide(order._id, item._id, key)
                                          }
                                        >
                                          <MDBIcon
                                            icon={icon}
                                            className="mr-1"
                                          />
                                          {key === "approve"
                                            ? "Approve"
                                            : "Deny"}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </MDBTable>
                  )}

                  {isReviewing &&
                    (() => {
                      const denied = order.items.filter((item) =>
                        isItemDenied(order, item),
                      ).length;
                      const approved = order.items.length - denied;
                      return (
                        <div className="px-3 py-2 border-top d-flex flex-wrap align-items-center justify-content-end grey lighten-5">
                          <small className="mr-auto">
                            <MDBBadge color="success" className="mr-1">
                              {approved}
                            </MDBBadge>
                            to approve
                            <MDBBadge color="danger" className="ml-3 mr-1">
                              {denied}
                            </MDBBadge>
                            to deny
                          </small>
                          <MDBBtn
                            size="sm"
                            color="primary"
                            className="my-0 mr-0"
                            disabled={isLoading}
                            onClick={() => handleReview(order)}
                          >
                            <MDBIcon icon="paper-plane" className="mr-1" />
                            {approved ? "Submit review" : "Deny whole request"}
                          </MDBBtn>
                        </div>
                      );
                    })()}

                  {(order.remarks || order.response) && (
                    <div className="px-3 py-1 small border-top">
                      {order.remarks && (
                        <span className="mr-3">
                          <b>Remarks:</b> {order.remarks}
                        </span>
                      )}
                      {order.response && (
                        <span>
                          <b>Response:</b> {order.response}
                        </span>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </MDBCardBody>

      <RequestModal
        show={showRequest}
        toggle={() => setShowRequest(false)}
        products={products}
        stock={stock}
        onSubmit={handleRequest}
        isLoading={isLoading}
      />
      <RequestModal
        show={!!adding}
        order={adding}
        toggle={() => setAdding(null)}
        products={products}
        stock={stock}
        onSubmit={handleAddItems}
        isLoading={isLoading}
      />
      <PricesModal
        order={pricing}
        toggle={() => setPricing(null)}
        onSubmit={handlePrices}
        isLoading={isLoading}
      />
      <ActionModal
        mode={action?.mode}
        order={action?.order}
        // the received and free qty entered on the list carry into the form
        receiveQtyOf={receiveQtyOf}
        freeQtyOf={freeQtyOf}
        toggle={() => setAction(null)}
        onSubmit={handleAction}
        isLoading={isLoading}
      />
    </MDBCard>
  );
}
